import type { RecentSearch } from '../repositories/savedSearchRepository';

/**
 * Normalizes text for fuzzy token matching.
 */
function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Returns true if target text matches the query using fuzzy subsequence matching.
 * Query tokens separated by whitespace must each match as subsequences in the target.
 */
export function fuzzyMatchesSearch(target: string, query: string): boolean {
  const normQuery = normalizeText(query);
  if (!normQuery) return true;

  const normTarget = normalizeText(target);
  if (!normTarget) return false;

  const queryTokens = normQuery.split(/\s+/).filter(Boolean);
  if (queryTokens.length === 0) return true;

  return queryTokens.every((token) => isSubsequence(token, normTarget));
}

function isSubsequence(needle: string, haystack: string): boolean {
  let needleIdx = 0;
  for (let i = 0; i < haystack.length && needleIdx < needle.length; i++) {
    if (haystack[i] === needle[needleIdx]) {
      needleIdx++;
    }
  }
  return needleIdx === needle.length;
}

/**
 * Deduplicates recent search runs by case-insensitive query/description,
 * filters against the optional query fuzzily, and caps the result to at most `maxCount` (default: 5).
 */
export function deduplicateRecentSearches(
  searches: RecentSearch[],
  maxCount: number = 5,
  query?: string,
): RecentSearch[] {
  const seen = new Set<string>();
  const result: RecentSearch[] = [];
  for (const s of searches) {
    const text = s.title || s.description || '';
    const key = text.trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) continue;
    seen.add(key);

    if (query && !fuzzyMatchesSearch(text, query)) {
      continue;
    }

    result.push(s);
    if (result.length >= maxCount) break;
  }
  return result;
}

export interface HighlightSegment {
  text: string;
  matched: boolean;
}

/**
 * Breaks target text into matched and unmatched segments for highlighting fuzzy matches.
 */
export function fuzzyHighlightSegments(target: string, query?: string): HighlightSegment[] {
  if (!target) return [];
  const normQuery = normalizeText(query || '');
  if (!normQuery) {
    return [{ text: target, matched: false }];
  }

  const queryTokens = normQuery.split(/\s+/).filter(Boolean);
  if (queryTokens.length === 0) {
    return [{ text: target, matched: false }];
  }

  const lowerTarget = target.toLowerCase();
  const matchedIndices = new Set<number>();

  for (const token of queryTokens) {
    let tokenIdx = 0;
    for (let i = 0; i < lowerTarget.length && tokenIdx < token.length; i++) {
      if (lowerTarget[i] === token[tokenIdx]) {
        matchedIndices.add(i);
        tokenIdx++;
      }
    }
  }

  const segments: HighlightSegment[] = [];
  let currentMatched: boolean | null = null;
  let currentChunk = '';

  for (let i = 0; i < target.length; i++) {
    const isMatch = matchedIndices.has(i);
    if (currentMatched === null) {
      currentMatched = isMatch;
      currentChunk = target[i];
    } else if (currentMatched === isMatch) {
      currentChunk += target[i];
    } else {
      segments.push({ text: currentChunk, matched: currentMatched });
      currentMatched = isMatch;
      currentChunk = target[i];
    }
  }

  if (currentChunk) {
    segments.push({ text: currentChunk, matched: !!currentMatched });
  }

  return segments;
}

